import React from 'react';
import { Dialog } from './dialog';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import Markdown from 'react-markdown';
import { useAiStore } from '../zustand-stores'; // Adjust path as needed
import { EUROPEAN_LANGUAGES } from '../../general/utilities';

interface AIExplainDialogProps {
    visible: boolean;
    onHide: () => void;
    content: string;
    isAiLoading: boolean;
    parent: any;
    onTranslate: (targetLang: string) => void;
    isTranslating: boolean;
    displayedLanguage: string;
    onShowPrompt?: () => void;
}

export const AIExplainDialog: React.FC<AIExplainDialogProps> = ({
    visible,
    onHide,
    content,
    isAiLoading: isStreaming,
    parent,
    onTranslate,
    isTranslating,
    displayedLanguage,
    onShowPrompt,
}) => {
    const { preferredLanguage, setPreferredLanguage } = useAiStore();
    const isSameLanguage: boolean = preferredLanguage === displayedLanguage;

    const footer = (
        <div className="flex justify-content-between align-items-center w-full">
            <div className="flex align-items-center gap-2">
                <span className="text-sm font-semibold">Translate to:</span>
                <Dropdown 
                    value={preferredLanguage} 
                    options={EUROPEAN_LANGUAGES} 
                    // optionLabel="name" 
                    // optionValue="code"
                    onChange={(e) => setPreferredLanguage(e.value)} 
                    placeholder="Select language"
                    disabled={isStreaming || isTranslating}
                    className="w-10rem"
                />
                <Button 
                    label="Go" 
                    icon="pi pi-language" 
                    onClick={() => onTranslate(preferredLanguage)}
                    disabled={isStreaming || isTranslating || !content || isSameLanguage}
                    className="p-button-sm p-button-outlined"
                />
                {onShowPrompt && (
                    <Button 
                        label="Show AI prompt" 
                        icon="pi pi-code" 
                        onClick={onShowPrompt}
                        className="p-button-sm p-button-info p-button-outlined"
                    />
                )}
            </div>
            <Button label="Close" icon="pi pi-times" onClick={onHide} className="p-button-text p-button-secondary" />
        </div>
    );

    const headerTemplate = (
        <div className="flex align-items-center gap-2">
            {/* Using a small SVG for the Union Jack ensures it works on Windows/Mac/Linux */}
            <svg width="24" height="16" viewBox="0 0 60 30" style={{ borderRadius: '2px' }}>
                <clipPath id="s"><path d="M0,0 v30 h60 v-30 z"/></clipPath>
                <path d="M0,0 v30 h60 v-30 z" fill="#012169"/>
                <path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" strokeWidth="6"/>
                <path d="M0,0 L60,30 M60,0 L0,30" stroke="#C8102E" strokeWidth="4"/>
                <path d="M30,0 v30 M0,15 h60" stroke="#fff" strokeWidth="10"/>
                <path d="M30,0 v30 M0,15 h60" stroke="#C8102E" strokeWidth="6"/>
            </svg>
            <span className="font-bold">British AI tutor</span>
        </div>
    );

    return (
        <Dialog 
            header={headerTemplate}
            visible={visible} 
            onHide={onHide}
            appendTo={parent}
            footer={footer}
            style={{ width: '50vw' }}
            contentStyle={{ height: '500px', overflowY: 'auto' }}
        >
            <div className="line-height-3" style={{ whiteSpace: 'pre-wrap' }}>
                <Markdown>{content}</Markdown>
                {isStreaming && <span className="ai-cursor">|</span>}
            </div>
            
            {(isStreaming || isTranslating) && (
                <div className="flex justify-content-center mt-3">
                    <i className="pi pi-spin pi-spinner" style={{ fontSize: '1.2rem' }}></i>
                </div>
            )}
        </Dialog>
    );
};
